import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { SessionService } from '@galaxy/core';
import { HostService } from '@vendasta/voice';
import { InvocationContextApp } from '@vendasta/ai-assistants';
import { PlatformLocation } from '../ai-assistant.constants';
import { PLATFORM_LOCATION_TOKEN } from '../tokens';

export const WebRTCCallState = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
} as const;

export type WebRTCCallState = (typeof WebRTCCallState)[keyof typeof WebRTCCallState];

export interface WebRTCCallError {
  message: string;
  code?: string;
  details?: unknown;
}

function isRTCIceServer(value: unknown): value is RTCIceServer {
  return typeof value === 'object' && value !== null && 'urls' in value;
}

function isRTCIceServerArray(value: unknown): value is RTCIceServer[] {
  return Array.isArray(value) && value.every(isRTCIceServer);
}

/**
 * WebRTCCallService handles voice communication with the voice backend using WebRTC.
 *
 * This service uses WebRTC for media transport and HTTP POST for signaling,
 * bypassing WebSocket infrastructure issues (HTTP/2 vs HTTP/1.1 routing).
 *
 * Key specifications:
 * - Audio: Opus codec via WebRTC (browser handles encoding/decoding)
 * - Signaling: HTTP POST endpoints (not WebSocket)
 * - Sample rate: 48kHz (WebRTC default), resampled to 24kHz on backend for OpenAI
 */
@Injectable()
export class WebRTCCallService {
  private static readonly ICE_GATHERING_TIMEOUT_MS = 5000;
  private static readonly ICE_CANDIDATE_BATCH_DELAY_MS = 100;
  private static readonly MUTE_STORAGE_KEY = 'webrtc-call-muted';

  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;
  private sessionId: string | null = null;

  private audioDataChannel: RTCDataChannel | null = null;
  private audioContext: AudioContext | null = null;

  private pendingIceCandidates: RTCIceCandidate[] = [];
  private iceCandidateSendTimer: ReturnType<typeof setTimeout> | undefined;

  private audioElement: HTMLAudioElement | null = null;

  private analyser: AnalyserNode | null = null;
  private analyserData: Uint8Array<ArrayBuffer> | null = null;

  private remoteAnalyser: AnalyserNode | null = null;
  private remoteAnalyserData: Uint8Array<ArrayBuffer> | null = null;

  public readonly callState = signal<WebRTCCallState>('idle');
  public readonly error = signal<WebRTCCallError | null>(null);
  public readonly remoteHangup = signal(false);
  public readonly isMuted = signal(localStorage.getItem(WebRTCCallService.MUTE_STORAGE_KEY) === 'true');
  readonly isRemoteAudioActive = signal(false);
  readonly isLocalAudioActive = signal(false);

  private audioPollingInterval: ReturnType<typeof setInterval> | null = null;
  private localCleanupInProgress = false;

  private readonly accountGroupId = signal<string | null>(null);
  private readonly partnerId = signal<string | null>(null);

  private readonly destroyRef = inject(DestroyRef);
  private readonly hostService = inject(HostService);
  private readonly iamSessionId = inject(SessionService).getSessionIdSignal();
  private readonly platformLocation = inject(PLATFORM_LOCATION_TOKEN);

  constructor() {
    this.destroyRef.onDestroy(() => this.cleanup());
  }

  private get baseUrl(): string {
    return this.hostService.hostWithScheme;
    // Override for local development — point to the locally running voice server.
    // return 'http://localhost:11001';
  }

  setNamespaces(agid: string | null, pid: string | null) {
    this.accountGroupId.set(agid);
    this.partnerId.set(pid);
  }

  async startCall(assistantId: string): Promise<void> {
    try {
      this.remoteHangup.set(false);
      this.setCallState(WebRTCCallState.CONNECTING);

      if (!this.iamSessionId()) {
        throw new Error('No active session — cannot authenticate WebRTC request');
      }

      this.sessionId = this.generateSessionId();
      await this.getUserMedia();
      const iceServers = await this.fetchICEConfig();
      this.createPeerConnection(iceServers);
      this.addLocalTracks();
      await this.createAndSendOffer(assistantId);
      this.setupLocalAudioAnalyser();

      this.setCallState(WebRTCCallState.CONNECTED);
    } catch (error) {
      this.handleError('Failed to start WebRTC call', error);
    }
  }

  async endCall(): Promise<void> {
    if (this.localCleanupInProgress) return;
    this.localCleanupInProgress = true;
    try {
      if (this.sessionId) {
        await this.closeSession();
      }

      this.cleanup();
      this.setCallState(WebRTCCallState.DISCONNECTED);
    } catch (error) {
      this.handleError('Failed to end call', error);
    } finally {
      this.localCleanupInProgress = false;
    }
  }

  private async getUserMedia(): Promise<void> {
    const constraints: MediaStreamConstraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: { ideal: 48000 },
        channelCount: 1,
      },
    };

    this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

    // Mute state is persisted in localStorage so it survives across calls and page reloads.
    if (this.isMuted()) {
      const audioTrack = this.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = false;
      }
    }
  }

  private async fetchICEConfig(): Promise<RTCIceServer[] | undefined> {
    try {
      const response = await fetch(`${this.baseUrl}/webrtc/ice-config`, {
        method: 'GET',
        headers: this.authHeaders,
      });
      if (!response.ok) {
        console.warn('[WebRTC] Failed to fetch ICE config, proceeding with STUN only');
        return undefined;
      }
      const data = await response.json();
      if (!isRTCIceServerArray(data?.ice_servers)) {
        console.warn('[WebRTC] Unexpected ICE config shape, proceeding with STUN only');
        return undefined;
      }
      return data.ice_servers;
    } catch (error) {
      console.warn('[WebRTC] Failed to fetch ICE config, proceeding with STUN only', error);
      return undefined;
    }
  }

  private createPeerConnection(serverICEServers?: RTCIceServer[]): void {
    const iceServers: RTCIceServer[] = [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ];

    if (serverICEServers) {
      iceServers.push(...serverICEServers);
    }

    const config: RTCConfiguration = { iceServers };

    this.peerConnection = new RTCPeerConnection(config);

    this.peerConnection.onicecandidate = (event) => this.handleIceCandidate(event);
    this.peerConnection.oniceconnectionstatechange = () => this.handleIceConnectionStateChange();
    this.peerConnection.onconnectionstatechange = () => this.handleConnectionStateChange();
    this.peerConnection.ontrack = (event) => this.handleTrack(event);
    this.peerConnection.ondatachannel = (event) => this.handleDataChannel(event);

    // Required for the server to be able to create its own DataChannel —
    // ensures SCTP transport is negotiated in the SDP
    this.peerConnection.createDataChannel('negotiation-trigger', { negotiated: false });

    this.audioContext = new AudioContext();
  }

  private handleDataChannel(event: RTCDataChannelEvent): void {
    if (event.channel.label !== 'audio-out') return;

    this.audioDataChannel = event.channel;

    this.audioDataChannel.onmessage = (msgEvent) => {
      if (typeof msgEvent.data === 'string') {
        this.handleControlMessage(msgEvent.data);
      }
    };

    this.audioDataChannel.onclose = () => {
      if (!this.localCleanupInProgress) {
        this.remoteHangup.set(true);
        this.cleanup();
        this.setCallState(WebRTCCallState.DISCONNECTED);
      }
    };

    this.audioDataChannel.onerror = (error) => {
      this.handleError('Audio DataChannel error', error);
    };
  }

  private handleControlMessage(data: string): void {
    try {
      const msg = JSON.parse(data);
      switch (msg.type) {
        case 'clear':
          break;
        case 'mark':
          this.sendMark(msg.name);
          break;
        case 'hangup':
          this.remoteHangup.set(true);
          this.cleanup();
          this.setCallState(WebRTCCallState.DISCONNECTED);
          break;
      }
    } catch {
      // Ignore non-JSON messages
    }
  }

  private sendMark(name: string): void {
    if (this.audioDataChannel?.readyState === 'open') {
      this.audioDataChannel.send(JSON.stringify({ type: 'mark', name }));
    }
  }

  private addLocalTracks(): void {
    if (!this.localStream || !this.peerConnection) {
      throw new Error('Local stream or peer connection not initialized');
    }

    this.localStream.getTracks().forEach((track) => {
      this.peerConnection!.addTrack(track, this.localStream!);
    });
  }

  private async createAndSendOffer(assistantId: string): Promise<void> {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialized');
    }

    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });

    await this.peerConnection.setLocalDescription(offer);
    await this.waitForIceGathering();

    const finalOffer = this.peerConnection.localDescription;
    if (!finalOffer) {
      throw new Error('No local description available');
    }

    const agid = this.accountGroupId();
    const pid = this.partnerId();
    const namespaceId = assistantId === 'ASSISTANT-system' ? 'SYSTEM' : agid || pid;
    const namespaceType = assistantId === 'ASSISTANT-system' ? 'System' : agid ? 'AccountGroup' : 'Partner';

    const response = await fetch(`${this.baseUrl}/webrtc/offer`, {
      method: 'POST',
      headers: this.authHeaders,
      body: JSON.stringify({
        session_id: this.sessionId,
        namespace_id: namespaceId,
        namespace_type: namespaceType,
        assistant_id: assistantId,
        app: this.getApp(),
        sdp: finalOffer.sdp,
        type: finalOffer.type,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server rejected offer: ${response.status} ${errorText}`);
    }

    const answer = await response.json();

    await this.peerConnection.setRemoteDescription(
      new RTCSessionDescription({
        type: answer.type,
        sdp: answer.sdp,
      }),
    );
  }

  private waitForIceGathering(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.peerConnection) {
        reject(new Error('No peer connection'));
        return;
      }

      if (this.peerConnection.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        resolve();
      }, WebRTCCallService.ICE_GATHERING_TIMEOUT_MS);

      this.peerConnection.onicegatheringstatechange = () => {
        if (this.peerConnection?.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          resolve();
        }
      };
    });
  }

  private handleIceCandidate(event: RTCPeerConnectionIceEvent): void {
    if (!event.candidate) return;

    this.pendingIceCandidates.push(event.candidate);

    if (this.iceCandidateSendTimer) {
      clearTimeout(this.iceCandidateSendTimer);
    }

    this.iceCandidateSendTimer = setTimeout(() => {
      this.sendBufferedIceCandidates();
    }, WebRTCCallService.ICE_CANDIDATE_BATCH_DELAY_MS);
  }

  private async sendBufferedIceCandidates(): Promise<void> {
    if (this.pendingIceCandidates.length === 0 || !this.sessionId) {
      return;
    }

    const candidates = [...this.pendingIceCandidates];
    this.pendingIceCandidates = [];

    for (const candidate of candidates) {
      try {
        await fetch(`${this.baseUrl}/webrtc/ice`, {
          method: 'POST',
          headers: this.authHeaders,
          body: JSON.stringify({
            session_id: this.sessionId,
            candidate: candidate.toJSON(),
          }),
        });
      } catch (error) {
        this.handleError('Failed to send ICE candidate', error);
      }
    }
  }

  private handleIceConnectionStateChange(): void {
    if (!this.peerConnection) return;

    switch (this.peerConnection.iceConnectionState) {
      case 'failed':
        this.handleError('ICE connection failed', null);
        break;
      case 'closed':
        if (!this.localCleanupInProgress) {
          this.remoteHangup.set(true);
          this.cleanup();
        }
        this.setCallState(WebRTCCallState.DISCONNECTED);
        break;
    }
  }

  private handleConnectionStateChange(): void {
    if (!this.peerConnection) return;

    switch (this.peerConnection.connectionState) {
      case 'connected':
        this.setCallState(WebRTCCallState.CONNECTED);
        break;
      case 'failed':
        this.handleError('Peer connection failed', null);
        break;
      case 'closed':
        if (!this.localCleanupInProgress) {
          this.remoteHangup.set(true);
          this.cleanup();
        }
        this.setCallState(WebRTCCallState.DISCONNECTED);
        break;
    }
  }

  private handleTrack(event: RTCTrackEvent): void {
    if (event.streams && event.streams[0]) {
      this.remoteStream = event.streams[0];
      this.playRemoteAudio(this.remoteStream);
      this.setupRemoteAudioAnalyser(this.remoteStream);
    }
  }

  private playRemoteAudio(stream: MediaStream): void {
    this.cleanupAudioElement();

    this.audioElement = new Audio();
    this.audioElement.srcObject = stream;
    this.audioElement.autoplay = true;
    this.audioElement.play().catch((error) => {
      this.handleError('Failed to play remote audio', error);
    });
  }

  private cleanupAudioElement(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.srcObject = null;
      this.audioElement = null;
    }
  }

  private async closeSession(): Promise<void> {
    try {
      await fetch(`${this.baseUrl}/webrtc/close`, {
        method: 'POST',
        headers: this.authHeaders,
        body: JSON.stringify({
          session_id: this.sessionId,
        }),
      });
    } catch (error) {
      this.handleError('Failed to close session on server', error);
    }
  }

  private setupLocalAudioAnalyser(): void {
    if (!this.localStream || !this.audioContext) return;

    const source = this.audioContext.createMediaStreamSource(this.localStream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.3;
    source.connect(this.analyser);
    this.analyserData = new Uint8Array(this.analyser.frequencyBinCount);
    this.startAudioPolling();
  }

  private startAudioPolling(): void {
    if (this.audioPollingInterval) return;
    this.audioPollingInterval = setInterval(() => {
      this.isLocalAudioActive.set(this.checkLocalAudio());
      this.isRemoteAudioActive.set(this.checkRemoteAudio());
    }, 100);
  }

  private checkLocalAudio(): boolean {
    if (!this.analyser || !this.analyserData) return false;

    this.analyser.getByteFrequencyData(this.analyserData);

    let sum = 0;
    for (let i = 0; i < this.analyserData.length; i++) {
      sum += this.analyserData[i];
    }
    return sum / this.analyserData.length > 10;
  }

  private setupRemoteAudioAnalyser(stream: MediaStream): void {
    if (!this.audioContext) return;

    this.audioContext.resume();
    const source = this.audioContext.createMediaStreamSource(stream);
    this.remoteAnalyser = this.audioContext.createAnalyser();
    this.remoteAnalyser.fftSize = 256;
    this.remoteAnalyser.smoothingTimeConstant = 0.3;
    source.connect(this.remoteAnalyser);
    this.remoteAnalyserData = new Uint8Array(this.remoteAnalyser.frequencyBinCount);
  }

  private checkRemoteAudio(): boolean {
    if (!this.remoteAnalyser || !this.remoteAnalyserData) return false;

    this.remoteAnalyser.getByteFrequencyData(this.remoteAnalyserData);

    let sum = 0;
    for (let i = 0; i < this.remoteAnalyserData.length; i++) {
      sum += this.remoteAnalyserData[i];
    }
    return sum / this.remoteAnalyserData.length > 5;
  }

  toggleMute(): void {
    if (!this.localStream) return;

    const audioTrack = this.localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      this.isMuted.set(!audioTrack.enabled);
      localStorage.setItem(WebRTCCallService.MUTE_STORAGE_KEY, String(this.isMuted()));
    }
  }

  private get authHeaders(): Record<string, string> {
    const sessionId = this.iamSessionId();
    if (!sessionId) {
      throw new Error('No active session — cannot authenticate WebRTC request');
    }
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`,
    };
  }

  private generateSessionId(): string {
    return `webrtc_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private setCallState(state: WebRTCCallState): void {
    if (this.callState() === state) return;
    this.callState.set(state);
  }

  private handleError(message: string, error?: unknown): void {
    console.error(`[WebRTC] ${message}`, error);
    this.cleanup();
    this.setCallState(WebRTCCallState.ERROR);
    this.error.set({ message, details: error });
  }

  private cleanup(): void {
    if (this.audioPollingInterval) {
      clearInterval(this.audioPollingInterval);
      this.audioPollingInterval = null;
    }
    this.isRemoteAudioActive.set(false);
    this.isLocalAudioActive.set(false);

    if (this.iceCandidateSendTimer) {
      clearTimeout(this.iceCandidateSendTimer);
      this.iceCandidateSendTimer = undefined;
    }
    this.pendingIceCandidates = [];

    if (this.audioDataChannel) {
      this.audioDataChannel.onmessage = null;
      this.audioDataChannel.onclose = null;
      this.audioDataChannel.onerror = null;
      this.audioDataChannel.close();
      this.audioDataChannel = null;
    }

    this.analyser = null;
    this.analyserData = null;
    this.remoteAnalyser = null;
    this.remoteAnalyserData = null;

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.cleanupAudioElement();

    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop());
      this.localStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    this.sessionId = null;
  }

  private getApp(): string {
    switch (this.platformLocation) {
      case PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
        return InvocationContextApp[InvocationContextApp.BUSINESS_CENTER];
      case PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
        return InvocationContextApp[InvocationContextApp.PARTNER_CENTER];
      default:
        return '';
    }
  }
}
